import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';

import { stationNetworkApi } from '../../lib/stationNetworkApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDateLongue, formatNombre } from '../../lib/formats';
import { PageHeader } from '../../components/ui/page-header';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { StatutBadge } from '../../components/ui/StatutBadge';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { normaliserRecherche } from '../../components/ui/searchable-select';
import { retenirStationCourante } from '../../components/stations/StationPicker';

/**
 * Réseau, vu du siège (lot 034) : une ligne par station pour une journée —
 * litres vendus, ventes, écart de caisse, écart de cuves sur 30 jours,
 * autonomie la plus basse, statut de la journée. La veille par défaut. Les
 * montants n'apparaissent qu'avec le droit « montants visibles » (le serveur
 * ne les envoie pas sans lui). Une case vide dit que le chiffre n'est pas encore
 * disponible, jamais un zéro inventé.
 */

/** Veille, en date locale AAAA-MM-JJ. */
const hier = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  const decalage = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - decalage).toISOString().slice(0, 10);
};

/** Autonomie sous ce nombre de jours : en rouge. */
const AUTONOMIE_BASSE = 2;

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground';

const signe = (valeur, decimales = 0) => {
  if (valeur === null || valeur === undefined) return '—';
  const nombre = Number(valeur);
  const texte = formatNombre(Math.abs(nombre), decimales);
  if (nombre > 0) return `+${texte}`;
  if (nombre < 0) return `−${texte}`;
  return texte;
};

const BarreEcart = ({ valeur, maximum, chaud }) => {
  if (valeur === null || valeur === undefined) return <span className="text-muted-foreground">—</span>;
  const largeur = maximum > 0 ? Math.min(100, (Math.abs(valeur) / maximum) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 min-w-[70px] flex-1 overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div
          className={`h-full rounded-full ${chaud ? 'bg-destructive' : 'bg-primary'}`}
          style={{ width: `${largeur}%` }}
        />
      </div>
      <span className={`tabular-nums ${chaud ? 'font-semibold text-destructive' : ''}`}>
        {signe(valeur, 2)} %
      </span>
    </div>
  );
};

const StationNetworkPage = () => {
  const navigate = useNavigate();
  const [jour, setJour] = useState(hier());
  const [recherche, setRecherche] = useState('');
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async (date) => {
    setChargement(true);
    try {
      setDonnees(await stationNetworkApi.summary(date));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger le réseau'));
      setDonnees(null);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger(jour);
  }, [jour, charger]);

  const lignes = useMemo(() => {
    const toutes = donnees?.stations || [];
    const mots = normaliserRecherche(recherche).split(/\s+/).filter(Boolean);
    if (!mots.length) return toutes;
    return toutes.filter((ligne) => {
      const botte = normaliserRecherche(`${ligne.name} ${ligne.city || ''} ${ligne.code || ''}`);
      return mots.every((mot) => botte.includes(mot));
    });
  }, [donnees, recherche]);

  const argent = Boolean(donnees?.money_visible);
  const totaux = donnees?.totals;
  const alertes = donnees?.alerts;
  const maximum = Math.max(0, ...lignes.map((ligne) => Math.abs(ligne.tank_variance_30d_percent ?? 0)));
  const pluriel = (n) => (n > 1 ? 's' : '');

  const ouvrirStation = (ligne) => {
    retenirStationCourante(ligne.station_id);
    navigate('/stations');
  };

  const pastilles = alertes
    ? [
        alertes.tank_variance.length > 0 && {
          niveau: 'critique',
          texte: `Écart de cuves : ${alertes.tank_variance.map((ligne) => ligne.name).join(', ')}`,
          court: alertes.tank_variance.length === 1 ? `${alertes.tank_variance[0].name} : écart cuve` : 'Écart de cuves',
          nombre: alertes.tank_variance.length,
        },
        alertes.cash_variance.length > 0 && {
          niveau: 'critique',
          texte: `Écart de caisse : ${alertes.cash_variance.map((ligne) => ligne.name).join(', ')}`,
          court: 'Écart de caisse',
          nombre: alertes.cash_variance.length,
        },
        alertes.days_not_stopped > 0 && {
          niveau: 'attention',
          texte: `${alertes.days_not_stopped} journée${pluriel(alertes.days_not_stopped)} non arrêtée${pluriel(alertes.days_not_stopped)}`,
          court: 'Journées non arrêtées',
          nombre: alertes.days_not_stopped,
        },
        alertes.low_tanks > 0 && {
          niveau: 'attention',
          texte: `${alertes.low_tanks} cuve${pluriel(alertes.low_tanks)} sous le seuil`,
          court: 'Cuves sous le seuil',
          nombre: alertes.low_tanks,
        },
        alertes.pump_checks_overdue > 0 && {
          niveau: 'attention',
          texte: `${alertes.pump_checks_overdue} pompe${pluriel(alertes.pump_checks_overdue)} à étalonner ou à inspecter`,
          court: 'Pompes en retard',
          nombre: alertes.pump_checks_overdue,
          action: { label: 'Voir les réglages', to: '/stations/reglages?onglet=registre' },
        },
      ].filter(Boolean)
    : [];

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations"
        title="Réseau"
        description="Une ligne par station : litres, ventes, écarts de caisse et de cuves, autonomie, journée."
        meta={donnees ? `${totaux.stations} station${pluriel(totaux.stations)}, ${formatDateLongue(donnees.date)}` : undefined}
        alertes={pastilles}
        actions={
          <>
            <Input
              type="search"
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
              placeholder="Chercher une station"
              aria-label="Chercher une station"
              className="h-9 w-48"
            />
            <Input
              type="date"
              value={jour}
              max={hier()}
              onChange={(event) => event.target.value && setJour(event.target.value)}
              aria-label="Journée"
              className="h-9 w-40"
            />
            <Button asChild type="button" variant="outline">
              <Link to="/stations/conformite">
                <FileText className="h-4 w-4" aria-hidden="true" />
                Conformité
              </Link>
            </Button>
          </>
        }
      />

      {chargement && !donnees ? (
        <LoadingState shape="table" />
      ) : !donnees || donnees.stations.length === 0 ? (
        <EmptyState variant="empty" title="Aucune station" className="border-0" />
      ) : (
        <section className="rounded-xl border border-border bg-card shadow-sm">
          <div className="overflow-x-auto px-1 py-2">
            <table className={CELLULES}>
              <thead>
                <tr>
                  <th className="text-left">Station</th>
                  <th className="text-left">Ville</th>
                  <th className="text-right">Litres vendus</th>
                  {argent && <th className="text-right">Ventes</th>}
                  {argent && <th className="text-right">Écart de caisse</th>}
                  <th className="text-left">Écart cuves sur 30 jours</th>
                  <th className="text-right">Autonomie la plus basse</th>
                  <th className="text-right">Journée</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => (
                  <tr key={ligne.station_id} className={ligne.flag_tank || ligne.flag_cash ? 'bg-destructive/5' : undefined}>
                    <td className="font-medium">
                      <button
                        type="button"
                        onClick={() => ouvrirStation(ligne)}
                        className="text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {ligne.name}
                      </button>
                    </td>
                    <td>{ligne.city || '—'}</td>
                    <td className="text-right tabular-nums">{formatNombre(ligne.liters_sold)}</td>
                    {argent && <td className="text-right tabular-nums">{formatNombre(ligne.sales_amount)}</td>}
                    {argent && (
                      <td className={`text-right tabular-nums ${ligne.flag_cash ? 'font-semibold text-destructive' : ''}`}>
                        {signe(ligne.cash_variance)}
                      </td>
                    )}
                    <td>
                      <BarreEcart valeur={ligne.tank_variance_30d_percent} maximum={maximum} chaud={ligne.flag_tank} />
                    </td>
                    <td
                      className={`text-right tabular-nums ${
                        ligne.min_autonomy_days !== null && ligne.min_autonomy_days < AUTONOMIE_BASSE ? 'font-semibold text-destructive' : ''
                      }`}
                    >
                      {ligne.min_autonomy_days === null ? '—' : `${formatNombre(ligne.min_autonomy_days, 1)} j`}
                    </td>
                    <td className="text-right">
                      {ligne.day_status ? (
                        <StatutBadge domaine="station_journee" code={ligne.day_status} ton={ligne.day_status === 'OPEN' ? 'warning' : undefined} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td colSpan={2}>Réseau</td>
                  <td className="text-right tabular-nums">{formatNombre(totaux.liters_sold)}</td>
                  {argent && <td className="text-right tabular-nums">{formatNombre(totaux.sales_amount)}</td>}
                  {argent && <td className="text-right tabular-nums">{signe(totaux.cash_variance)}</td>}
                  <td className="tabular-nums">
                    {totaux.tank_variance_30d_percent === null ? '' : `${signe(totaux.tank_variance_30d_percent, 2)} %`}
                  </td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default StationNetworkPage;
